import { useState, useRef, useEffect, useMemo } from 'react';
import {
  AT_MENTION_CATEGORIES,
  AT_MENTION_ENTITIES,
  AT_MENTION_TEMPLATE_TOKENS,
} from '@/config/static/mentions';
import { useProvisionedUsers } from '@/hooks/useProvisionedUsers';
import { cn } from '@/utils/helpers';
import {
  MentionCategoryIcon,
  MentionPersonRowIcon,
  type MentionCategoryId,
} from './MentionCategoryIcons';

interface AtMentionDropdownProps {
  mode?: 'entity' | 'template';
  placement?: 'popover' | 'inline';
  query?: string;
  /** Open directly on a category (e.g. from Users / Devices tabs). */
  initialCategoryId?: string | null;
  onSelect: (token: string) => void;
  onClose: () => void;
  className?: string;
}

export function AtMentionDropdown({
  mode = 'entity',
  placement = 'popover',
  query = '',
  initialCategoryId = null,
  onSelect,
  onClose,
  className,
}: AtMentionDropdownProps) {
  const { mentionEntities: provisionedUserMentions } = useProvisionedUsers();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialCategoryId);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSelectedCategory(initialCategoryId);
    setSearch('');
  }, [initialCategoryId]);

  useEffect(() => {
    if (selectedCategory) {
      searchInputRef.current?.focus();
    }
  }, [selectedCategory]);

  const templateTokens = useMemo(() => {
    const q = (query || search).trim().toLowerCase();
    if (!q) return AT_MENTION_TEMPLATE_TOKENS;
    return AT_MENTION_TEMPLATE_TOKENS.filter(
      (t) =>
        t.token.includes(q) ||
        t.label.toLowerCase().includes(q) ||
        t.subtitle.toLowerCase().includes(q)
    );
  }, [query, search]);

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return AT_MENTION_CATEGORIES;
    return AT_MENTION_CATEGORIES.filter((cat) => cat.label.toLowerCase().includes(q));
  }, [query]);

  const categoryEntities =
    selectedCategory === 'users'
      ? provisionedUserMentions
      : selectedCategory
        ? (AT_MENTION_ENTITIES[selectedCategory] ?? [])
        : [];

  const entities = selectedCategory
    ? categoryEntities.filter(
        (e) =>
          e.name.toLowerCase().includes(search.toLowerCase()) ||
          e.subtitle.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const rootClass = cn(
    'at-mention',
    mode === 'template' && 'at-mention--template',
    placement === 'inline' && 'at-mention--inline',
    className
  );

  const handleBack = () => {
    setSelectedCategory(null);
    setSearch('');
  };

  if (mode === 'template') {
    return (
      <div className={rootClass}>
        <p className="at-mention__hint">Choose an entity type</p>
        <ul className="at-mention__list">
          {templateTokens.length === 0 ? (
            <li className="at-mention__empty">No matching tokens</li>
          ) : (
            templateTokens.map((t) => (
              <li key={t.token}>
                <button
                  type="button"
                  className="at-mention__entity"
                  onClick={() => onSelect(t.token)}
                >
                  <span className="at-mention__entity-icon" aria-hidden>
                    <MentionPersonRowIcon size={20} />
                  </span>
                  <div className="at-mention__entity-info">
                    <span className="at-mention__entity-name">{t.label}</span>
                    <span className="at-mention__entity-sub">{t.subtitle}</span>
                  </div>
                </button>
              </li>
            ))
          )}
        </ul>
        <button type="button" className="at-mention__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
    );
  }

  return (
    <div className={rootClass} role="listbox" aria-label="Mention picker">
      {!selectedCategory ? (
        <>
          <p className="at-mention__hint">What do you want to @ mention?</p>
          <ul className="at-mention__list at-mention__list--categories">
            {filteredCategories.length === 0 ? (
              <li className="at-mention__empty">No matching categories</li>
            ) : (
              filteredCategories.map((cat) => (
                <li key={cat.id}>
                  <button
                    type="button"
                    className="at-mention__item"
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    <span className="at-mention__item-icon" aria-hidden>
                      <MentionCategoryIcon categoryId={cat.id as MentionCategoryId} size={18} />
                    </span>
                    <span className="at-mention__item-label">{cat.label}</span>
                    <svg
                      className="at-mention__item-chevron"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M4.5 3L7.5 6L4.5 9"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      ) : (
        <>
          <div className="at-mention__search-row">
            <button
              type="button"
              className="at-mention__back"
              onClick={handleBack}
              aria-label="Back to categories"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path
                  d="M7.5 3L4.5 6L7.5 9"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="at-mention__search-field">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.3" />
                <path d="M10 10l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              <input
                ref={searchInputRef}
                className="at-mention__search-input"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <ul className="at-mention__list at-mention__list--entities">
            {entities.length === 0 ? (
              <li className="at-mention__empty">No results</li>
            ) : (
              entities.map((entity) => (
                <li key={entity.id}>
                  <button
                    type="button"
                    className="at-mention__entity"
                    onClick={() => onSelect(entity.name)}
                  >
                    <span className="at-mention__entity-icon" aria-hidden>
                      <MentionPersonRowIcon size={20} />
                    </span>
                    <div className="at-mention__entity-info">
                      <span className="at-mention__entity-name">{entity.name}</span>
                      <span className="at-mention__entity-sub">{entity.subtitle}</span>
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      )}
      {placement === 'inline' ? (
        <button type="button" className="at-mention__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      ) : null}
    </div>
  );
}
